import { useState, useEffect, useCallback, useRef } from 'react';
import { bffQuery, bffMutation } from '../lib/bffClient';
import { canRefund, refundableOf, refundNeedsReference } from '../lib/billing';

// ── Fragments ─────────────────────────────────────────────────────────────────

const LINE_ITEM_FIELDS = `
  currency id invoiceId itemType sourceId description quantity
  # WHICH service the line bills, on whose terms, by whose hand, and what KIND
  # of line it is (docs/CLINIC_RECEPTION_SERVICE_INVOICE_DESIGN.md §7.1). The
  # role is what locks the PRIMARY line — the encounter's own service, changed
  # by re-pointing the APPOINTMENT and never by editing the invoice.
  catalogId serviceDeclarationId priceSource role serviceLabel
  unitTariff discountType discountValue
  totalAmount discount insuranceShare patientPayable settlementStatus
  insurerId patientSharePct insuranceSource insuranceOverrideReason computedInsuranceShare
`;

// paidAmount/outstanding are derived by the service from the payments themselves.
// Before they existed the UI guessed "paid" from the lines' settlement flags, which
// settle all-or-nothing — so a partly-paid invoice showed ۰ collected.
const INVOICE_FIELDS = `
  currency id patientId doctorId clinicId appointmentId createdAt updatedAt
  discountType discountValue
  totalAmount invoiceDiscount discountTotal insuranceShare patientPayable status shiftId
  paidAmount outstanding
  kind issuedAt invoiceNumber appointmentTime isIssued
  # Derived by the service from its own lines: which declaration priced the
  # encounter's service, and whether anything priced it at all.
  primaryServiceDeclarationId priceResolution
  patientName patientPhone doctorName
  lineItems { ${LINE_ITEM_FIELDS} }
`;

const PAYMENT_FIELDS = `currency id invoiceId patientId amount paymentMethod referenceNumber createdAt shiftId status kind refundOf refundedAmount`;

// ── Queries ───────────────────────────────────────────────────────────────────

// doctorId omitted/empty = every doctor in the clinic.
const LIST_INVOICES = `
  query AccountingInvoices($status: String, $patientId: String, $from: String, $to: String, $doctorId: String) {
    accountingInvoices(status: $status, patientId: $patientId, from: $from, to: $to, doctorId: $doctorId) { ${INVOICE_FIELDS} }
  }
`;

// Server-PAGED cashier list. status picks the tab (queue = OPEN, partial, settled = PAID);
// items is that tab's page; total is the tab's whole size (its badge); outstandingTotal /
// collectedTotal are aggregates over the whole tab, so «مجموع بدهی» / «دریافتی» count rows
// off the visible page. search matches patient name/phone, resolved to ids server-side.
const LIST_INVOICES_PAGE = `
  query AccountingInvoicesPage($status: String, $doctorId: String, $search: String, $from: String, $to: String, $limit: Int, $offset: Int) {
    accountingInvoicesPage(status: $status, doctorId: $doctorId, search: $search, from: $from, to: $to, limit: $limit, offset: $offset) {
      currency
      items { ${INVOICE_FIELDS} }
      total outstandingTotal collectedTotal
    }
  }
`;

const GET_INVOICE = `
  query AccountingInvoice($invoiceId: ID!) {
    accountingInvoice(invoiceId: $invoiceId) { ${INVOICE_FIELDS} }
  }
`;

const DAILY_SUMMARY = `
  query AccountingDailySummary($doctorId: String) {
    accountingDailySummary(doctorId: $doctorId) {
      currency
      clinicId doctorId date visitRevenue medicalServiceRevenue productRevenue totalRevenue
      totalCash totalCard totalOnline totalInsurance totalPatientDebt activeShiftId
    }
  }
`;

const SHIFT_SUMMARY = `
  query AccountingShiftSummary($shiftId: ID!) {
    accountingShiftSummary(shiftId: $shiftId) {
      currency
      shift { currency id doctorId clinicId openedAt closedAt status totalCash totalCard totalOnline totalInsurance notes }
      totalInvoices paidInvoices totalCollected
    }
  }
`;

const CLINIC_SERVICE_DECLARATIONS = `
  query ClinicServiceDeclarations {
    clinicServiceDeclarations { currency id catalogId description unitPrice isActive doctorId orderType }
  }
`;

// Just the id/name pairs the doctor filter needs. useClinicDoctors() would also do
// a doctorInfo round-trip per doctor to enrich rows the dropdown never shows.
const CLINIC_DOCTOR_OPTIONS = `
  query MyClinicDoctors($clinicId: ID!) {
    listClinicDoctors(clinicId: $clinicId) { doctorId doctorName active }
  }
`;

const MY_CLINIC = `query MyClinic { myClinic { id } }`;

const LIST_PAYMENTS = `
  query AccountingPayments($invoiceId: ID) {
    accountingPayments(invoiceId: $invoiceId) { ${PAYMENT_FIELDS} }
  }
`;

// Every payment taken in one cashier shift — the drawer's cash log, which the totals
// on the shift panel are the sum of.
const SHIFT_PAYMENTS = `
  query ShiftPayments($shiftId: ID!) {
    accountingPayments(shiftId: $shiftId) { ${PAYMENT_FIELDS} }
  }
`;

// ── Mutations ─────────────────────────────────────────────────────────────────

const CREATE_INVOICE = `
  mutation AccountingCreateInvoice($patientId: String!, $doctorId: String!, $appointmentId: String, $shiftId: String) {
    accountingCreateInvoice(patientId: $patientId, doctorId: $doctorId, appointmentId: $appointmentId, shiftId: $shiftId) { ${INVOICE_FIELDS} }
  }
`;

const ADD_LINE_ITEM = `
  mutation AccountingAddLineItem($input: AddLineItemInput!) {
    accountingAddLineItem(input: $input) { ${LINE_ITEM_FIELDS} }
  }
`;

const UPDATE_LINE_ITEM = `
  mutation AccountingUpdateLineItem($input: UpdateLineItemInput!) {
    accountingUpdateLineItem(input: $input) { ${LINE_ITEM_FIELDS} }
  }
`;

// Replacing a COMPUTED insurer share with a human's number. Deliberately not part
// of updateLineItem: an override is a different act with a different permission
// (`payments.manage`) and a mandatory reason, and the service keeps the computed
// figure beside it so the invoice still shows what the rules said.
const OVERRIDE_LINE_INSURANCE = `
  mutation OverrideLineInsurance($lineItemId: ID!, $insuranceShare: Float!, $reason: String!) {
    overrideLineInsurance(
      lineItemId: $lineItemId, insuranceShare: $insuranceShare, reason: $reason
    ) { ${LINE_ITEM_FIELDS} }
  }
`;

const REMOVE_LINE_ITEM = `
  mutation AccountingRemoveLineItem($lineItemId: ID!) {
    accountingRemoveLineItem(lineItemId: $lineItemId)
  }
`;

// `reason` is MANDATORY for a non-zero discount: the service refuses one without
// it (REASON_REQUIRED), because reducing a price is a judgement and a ledger that
// records the judgement without its justification can be read but not audited.
const SET_INVOICE_DISCOUNT = `
  mutation AccountingSetInvoiceDiscount($invoiceId: ID!, $discountType: String!, $discountValue: Float!, $reason: String) {
    accountingSetInvoiceDiscount(invoiceId: $invoiceId, discountType: $discountType, discountValue: $discountValue, reason: $reason) { ${INVOICE_FIELDS} }
  }
`;

const RECORD_PAYMENT = `
  mutation AccountingRecordPayment($input: RecordPaymentInput!) {
    accountingRecordPayment(input: $input) { ${PAYMENT_FIELDS} }
  }
`;

// `reason` is required by the schema now — a reversal with no account of itself
// is exactly what the journal exists to prevent.
// «در انتظار تأیید» — a POS or online payment counts for nothing until the
// terminal answers. Resolving it is what turns it into money (or removes it).
const RESOLVE_PAYMENT = `
  mutation AccountingResolvePayment($paymentId: ID!, $outcome: String!, $referenceNumber: String) {
    accountingResolvePayment(paymentId: $paymentId, outcome: $outcome, referenceNumber: $referenceNumber) {
      ${PAYMENT_FIELDS}
    }
  }
`;

const VOID_INVOICE = `
  mutation AccountingVoidInvoice($invoiceId: ID!, $reason: String!) {
    accountingVoidInvoice(invoiceId: $invoiceId, reason: $reason) { ${INVOICE_FIELDS} }
  }
`;

// The proforma becomes the numbered FINAL invoice; every prepayment on it stays.
const ISSUE_INVOICE = `
  mutation AccountingIssueInvoice($invoiceId: ID!) {
    accountingIssueInvoice(invoiceId: $invoiceId) { ${INVOICE_FIELDS} }
  }
`;

// «علی‌الحساب» — what the clinic holds on account for a patient, and spending it.
const PATIENT_CREDIT_BALANCE = `
  query PatientCreditBalance($patientId: ID!) {
    patientCreditBalance(patientId: $patientId) { currency balanceRial depositedRial appliedRial refundedRial }
  }
`;

const APPLY_CREDIT = `
  mutation ApplyCreditToInvoice($invoiceId: ID!, $amountRial: Float!, $note: String) {
    applyCreditToInvoice(invoiceId: $invoiceId, amountRial: $amountRial, note: $note) {
      currency
      balanceRial
      payment { ${PAYMENT_FIELDS} }
      invoice { ${INVOICE_FIELDS} }
    }
  }
`;

const RECORD_PATIENT_CREDIT = `
  mutation RecordPatientCredit($patientId: ID!, $amountRial: Float!, $paymentMethod: String, $planRef: String, $note: String, $requestId: ID) {
    recordPatientCredit(patientId: $patientId, amountRial: $amountRial, paymentMethod: $paymentMethod, planRef: $planRef, note: $note, requestId: $requestId) {
      currency
      balanceRial
      credit { currency id amountRial source paymentMethod createdAt }
    }
  }
`;

const OPEN_SHIFT = `
  mutation AccountingOpenShift {
    accountingOpenShift { currency id status openedAt totalCash totalCard totalOnline totalInsurance }
  }
`;

const CLOSE_SHIFT = `
  mutation AccountingCloseShift($shiftId: ID!, $notes: String) {
    accountingCloseShift(shiftId: $shiftId, notes: $notes) { currency id status closedAt totalCash totalCard totalOnline totalInsurance }
  }
`;

// ── Status helpers ────────────────────────────────────────────────────────────

// ── Main hook ─────────────────────────────────────────────────────────────────

/**
 * @param {string} [doctorId] Narrow to one doctor. Falsy (the default) = every
 *   doctor in the clinic, which is what a cashier desk wants to see.
 * @param {{fromTs: number, toTs: number}|null} [range] The date window, in epoch ms.
 *   CONTROLLED by the caller — the cash desk drives it from the URL, so a refresh
 *   restores the same window. null (the default) = no bound; Reception relies on
 *   that to match appointments against their invoices whenever those were created.
 *
 * The window used to be state *inside* this hook, defaulting to null, which the cash
 * desk then overwrote with "today" from an effect. That meant two fetches on mount —
 * an unbounded one and a today-scoped one — racing each other. The unbounded query is
 * the bigger and slower of the two, so it usually landed last and won, leaving the desk
 * showing every invoice ever while the filter chip read «امروز». The next reload (after
 * a payment, or on closing the drawer) issued one correctly-scoped fetch, which replaced
 * that stale list — and if none of those invoices were raised today, the desk went blank.
 */
export function useCashier(doctorId = '', range = null, opts = {}) {
  // Pagination is OPT-IN (the cashier list passes pageSize). Callers that don't — the
  // Reception panel, which matches EVERY appointment to its invoice and must not miss one to a
  // page boundary — keep the legacy behaviour: every invoice, unpaginated. (Its own
  // scoping keeps that bounded; this hook does not change how much it asks for.)
  const paginated = opts.pageSize != null;
  const { tab = 'queue', page = 1, pageSize = 50, search = '' } = opts;
  // The badge counts, the KPI aggregates and the day summary are the LIST
  // screen's furniture. Reception wants one thing from this hook — the invoice
  // behind each appointment — and asking for the rest cost it four extra round
  // trips on every day change, for figures «مالی امروز» now answers properly
  // (docs/CLINIC_RECEPTION_FINANCIAL_EVENTS_DESIGN.md §11).
  const badges = opts.badges !== false;
  // `list: false` — the MUTATIONS only, for a host that was handed the one invoice
  // it settles (components/billing/SettleHost.jsx). Without it that host would fire
  // an invoice query nothing on screen reads, on mount and again after every
  // payment, purely to obtain `recordPayment`.
  const wantsList = opts.list !== false;
  // The tab IS a status filter now: 'all' = no status (every invoice in the range),
  // the others map to one status. Only the active tab's rows are fetched; the three
  // real statuses come back as a count + aggregate (limit 1) for the badges and KPIs.
  const STATUS_FOR = { all: '', queue: 'OPEN', partial: 'INVOICE_PARTIALLY_PAID', settled: 'PAID' };
  const activeStatus = tab in STATUS_FOR ? STATUS_FOR[tab] : 'OPEN';

  const [invoices, setInvoices]     = useState([]);   // the active tab's page only
  const [meta, setMeta]             = useState({ total: 0, counts: { queue: 0, partial: 0, settled: 0 }, dueQueue: 0, collected: 0 });
  const [dailySummary, setDailySummary] = useState(null);
  const [loading, setLoading]       = useState(wantsList);
  const [error, setError]           = useState(null);

  // Identity, not reference: the caller rebuilds `range` on every render, and depending
  // on the object itself would refetch forever.
  const fromTs = range?.fromTs ?? null;
  const toTs   = range?.toTs ?? null;

  // Guards against an out-of-order response. Switching doctor/window/tab/page fires a new
  // fetch while the previous one is still in flight, and the loser must not land on top.
  const reqId = useRef(0);

  const loadAll = useCallback(async () => {
    if (!wantsList) return;
    const req = ++reqId.current;
    setLoading(true);
    // Clear the previous failure BEFORE the fetch. It used to be set on catch and
    // never unset, so one blip left the mobile surface's ErrorState on screen for
    // the rest of the session — including after a retry that succeeded.
    setError(null);
    const win = fromTs != null && toTs != null
      ? { from: String(fromTs), to: String(toTs) }
      : {};
    const common = { doctorId, search: search || null, ...win };
    const EMPTY = { items: [], total: 0, outstandingTotal: 0, collectedTotal: 0 };
    try {
      if (!paginated) {
        // Legacy path (Reception): every invoice across the three live statuses, merged,
        // so an appointment can be matched to its invoice whatever its state or position.
        const [dash, openData, partialData, paidData] = await Promise.all([
          bffQuery(DAILY_SUMMARY, { doctorId }),
          bffQuery(LIST_INVOICES, { status: 'OPEN', doctorId, ...win }),
          bffQuery(LIST_INVOICES, { status: 'INVOICE_PARTIALLY_PAID', doctorId, ...win }),
          bffQuery(LIST_INVOICES, { status: 'PAID', doctorId, ...win }),
        ]);
        if (req !== reqId.current) return;
        const open    = openData.accountingInvoices || [];
        const partial = partialData.accountingInvoices || [];
        const paid    = paidData.accountingInvoices || [];
        setInvoices([...open, ...partial, ...paid]);
        setMeta({
          total: open.length + partial.length + paid.length,
          counts: { queue: open.length, partial: partial.length, settled: paid.length },
          dueQueue: 0, collected: 0,   // this path's callers read `invoices`, not the KPIs
        });
        setDailySummary(dash.accountingDailySummary || null);
        return;
      }
      // The active tab's page (status may be '' for «همه»), plus one bounded row per real
      // status for the badges + KPI aggregates, plus the daily summary. All bounded — no query
      // can pull the whole (post-migration) invoice set into one response.
      const activePage = bffQuery(LIST_INVOICES_PAGE, { ...common, status: activeStatus, limit: pageSize, offset: (Math.max(1, page) - 1) * pageSize });
      const [dash, activeRes, openRes, partialRes, settledRes] = badges
        ? await Promise.all([
          bffQuery(DAILY_SUMMARY, { doctorId }),
          activePage,
          bffQuery(LIST_INVOICES_PAGE, { ...common, status: 'OPEN', limit: 1, offset: 0 }),
          bffQuery(LIST_INVOICES_PAGE, { ...common, status: 'INVOICE_PARTIALLY_PAID', limit: 1, offset: 0 }),
          bffQuery(LIST_INVOICES_PAGE, { ...common, status: 'PAID', limit: 1, offset: 0 }),
        ])
        : [null, await activePage, null, null, null];
      if (req !== reqId.current) return;   // superseded — drop it
      const active  = activeRes?.accountingInvoicesPage  || EMPTY;
      const open    = openRes?.accountingInvoicesPage    || EMPTY;
      const partial = partialRes?.accountingInvoicesPage || EMPTY;
      const settled = settledRes?.accountingInvoicesPage || EMPTY;
      const byStatus = { OPEN: open, 'INVOICE_PARTIALLY_PAID': partial, PAID: settled };
      setInvoices(active.items || []);
      setMeta({
        total: active.total || 0,
        counts: {
          queue:   byStatus['OPEN'].total || 0,
          partial: byStatus['INVOICE_PARTIALLY_PAID'].total || 0,
          settled: byStatus['PAID'].total || 0,
        },
        // «مجموع بدهی» is the unsettled money: OPEN + PARTIAL outstanding across the range.
        dueQueue:  (byStatus['OPEN'].outstandingTotal || 0) + (byStatus['INVOICE_PARTIALLY_PAID'].outstandingTotal || 0),
        // «دریافتی» is everything collected in the range, whatever the invoice's status.
        collected: (byStatus['OPEN'].collectedTotal || 0) + (byStatus['INVOICE_PARTIALLY_PAID'].collectedTotal || 0) + (byStatus['PAID'].collectedTotal || 0),
      });
      // Null, not zero: a caller that did not ask for the summary has no figure,
      // and a stale one left standing would be read as this range's.
      setDailySummary(dash ? (dash.accountingDailySummary || null) : null);
    } catch (e) {
      if (req !== reqId.current) return;
      setError(e);
    } finally {
      if (req === reqId.current) setLoading(false);
    }
  }, [fromTs, toTs, doctorId, tab, page, pageSize, search, paginated, badges, wantsList]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const getInvoice = useCallback(async (invoiceId) => {
    const data = await bffQuery(GET_INVOICE, { invoiceId });
    return data.accountingInvoice;
  }, []);

  const getPayments = useCallback(async (invoiceId) => {
    const data = await bffQuery(LIST_PAYMENTS, { invoiceId });
    return data.accountingPayments || [];
  }, []);

  const getShiftPayments = useCallback(async (shiftId) => {
    const data = await bffQuery(SHIFT_PAYMENTS, { shiftId }, 'ShiftPayments');
    return data.accountingPayments || [];
  }, []);

  const createInvoice = useCallback(async ({ patientId, doctorId: forDoctorId, appointmentId, shiftId }) => {
    const data = await bffMutation(CREATE_INVOICE, { patientId, doctorId: forDoctorId, appointmentId, shiftId });
    const inv = data.accountingCreateInvoice;
    setInvoices(prev => [inv, ...prev]);
    return inv;
  }, []);

  const addLineItem = useCallback(async (input) => {
    const data = await bffMutation(ADD_LINE_ITEM, { input });
    const item = data.accountingAddLineItem;
    setInvoices(prev => prev.map(inv =>
      inv.id === input.invoiceId
        ? {
            ...inv,
            lineItems: [...inv.lineItems, item],
            totalAmount: inv.totalAmount + item.totalAmount,
            patientPayable: inv.patientPayable + item.patientPayable,
            // The service reopens a settled invoice when a new item lands on it
            // (payments no longer cover the payable). Mirror that here so the
            // cashier list and the reception row stop showing «تسویه‌شده».
            status: inv.status === 'PAID' ? 'INVOICE_PARTIALLY_PAID' : inv.status,
          }
        : inv
    ));
    return item;
  }, []);

  const updateLineItem = useCallback(async (input) => {
    const data = await bffMutation(UPDATE_LINE_ITEM, { input });
    // An edit re-derives the invoice's totals AND its status server-side: repricing
    // a line up reopens a settled invoice, repricing it down can settle one. Status
    // depends on the paid total, which the list doesn't carry — refetch, don't guess.
    await loadAll();
    return data.accountingUpdateLineItem;
  }, [loadAll]);

  const overrideLineInsurance = useCallback(async (lineItemId, insuranceShare, reason) => {
    const data = await bffMutation(OVERRIDE_LINE_INSURANCE, { lineItemId, insuranceShare, reason });
    // Same reason updateLineItem refetches: the share moves what the patient owes,
    // which can settle or reopen the invoice.
    await loadAll();
    return data.overrideLineInsurance;
  }, [loadAll]);

  const setInvoiceDiscount = useCallback(async (invoiceId, discountType, discountValue, reason) => {
    const data = await bffMutation(SET_INVOICE_DISCOUNT, {
      invoiceId, discountType, discountValue, reason: reason || '',
    });
    const updated = data.accountingSetInvoiceDiscount;
    // Like an edit, a discount can settle or reopen the invoice — the server returns the
    // whole re-derived invoice, so take it wholesale rather than patching a delta.
    setInvoices(prev => prev.map(inv => (inv.id === invoiceId ? { ...inv, ...updated } : inv)));
    return updated;
  }, []);

  const removeLineItem = useCallback(async (invoiceId, lineItemId) => {
    await bffMutation(REMOVE_LINE_ITEM, { lineItemId });
    setInvoices(prev => prev.map(inv =>
      inv.id === invoiceId
        ? { ...inv, lineItems: inv.lineItems.filter(li => li.id !== lineItemId) }
        : inv
    ));
  }, []);

  const recordPayment = useCallback(async (input) => {
    const data = await bffMutation(RECORD_PAYMENT, { input });
    await loadAll();
    return data.accountingRecordPayment;
  }, [loadAll]);

  /**
   * Hand money back FROM THE DESK.
   *
   * The same act as the ledger page's refund and deliberately the same shape: a
   * new signed row against a real payment, banked into the drawer open NOW, so a
   * closed shift is never rewritten and both facts stay in the ledger. It lives
   * here as well because reception is where money is taken, and until now the
   * desk could take it and could not give it back
   * (docs/CLINIC_RECEPTION_FINANCIAL_EVENTS_DESIGN.md §8.3).
   */
  const refundPayment = useCallback(async ({ paymentId, amount, paymentMethod, referenceNumber, reason, requestId }) => {
    const data = await bffMutation(REFUND_PAYMENT, {
      paymentId,
      amount,
      paymentMethod: paymentMethod || null,
      referenceNumber: referenceNumber || null,
      reason: reason || '',
      requestId: requestId || null,
    }, 'AccountingRefundPayment');
    await loadAll();
    return data.accountingRefundPayment;
  }, [loadAll]);

  /**
   * Settle a pending POS / online payment (§R18).
   *
   * `outcome` is SUCCESS | FAILED | CANCELLED. A pending row is money the clinic
   * has NOT got: until the terminal answers it is in no total, no drawer and no
   * day position, which is why the rail lists one older than fifteen minutes as
   * work rather than as takings.
   */
  const resolvePayment = useCallback(async (paymentId, outcome, referenceNumber) => {
    const data = await bffMutation(RESOLVE_PAYMENT, {
      paymentId, outcome, referenceNumber: referenceNumber || null,
    }, 'AccountingResolvePayment');
    await loadAll();
    return data.accountingResolvePayment;
  }, [loadAll]);

  const voidInvoice = useCallback(async (invoiceId, reason) => {
    const data = await bffMutation(VOID_INVOICE, { invoiceId, reason: reason || null });
    const updated = data.accountingVoidInvoice;
    setInvoices(prev => prev.map(inv => inv.id === invoiceId ? updated : inv));
    return updated;
  }, []);

  /**
   * «برگشت وجه و ابطال فاکتور» — the whole resolution, in the order it has to
   * happen (docs/CLINIC_RECEPTION_FINANCIAL_EVENTS_DESIGN.md §R11).
   *
   * A cancel refused because money was taken is resolved by giving the money
   * back FIRST and voiding after: an invoice whose payment sits in a CLOSED
   * shift cannot be voided at all (accounting core §10.2), and refunding into
   * today's open drawer is what physically happens anyway.
   *
   * Not atomic, and it cannot be — three services, three writes. So it is
   * ORDERED instead: every refund lands before the void is attempted, and a
   * refund that fails stops the sequence with the earlier ones already in the
   * journal. That is the recoverable failure. The other order (void first)
   * would leave a voided invoice with live payments against it.
   *
   * @returns {{refunded: number}} how many payments went back
   */
  const refundAllAndVoid = useCallback(async (invoiceId, { reason, referenceNumber } = {}) => {
    if (!String(reason || '').trim()) throw new Error('reason is required');
    const rows = await getPayments(invoiceId);
    const going = rows.filter((row) => canRefund(row));
    for (const row of going) {
      const method = row.paymentMethod || 'CASH';
      await refundPayment({
        paymentId: row.id,
        amount: refundableOf(row),
        paymentMethod: method,
        // A transfer or terminal refund needs its reference; cash does not, and
        // sending an empty one would be a made-up fact about a bank movement.
        referenceNumber: refundNeedsReference(method) ? (referenceNumber || undefined) : undefined,
        reason,
        requestId: `refund-void-${invoiceId}-${row.id}`,
      });
    }
    await voidInvoice(invoiceId, reason);
    return { refunded: going.length };
  }, [getPayments, refundPayment, voidInvoice]);

  // Issuance freezes the lines and stamps the number; the row itself does not move,
  // so the list is patched in place.
  const issueInvoice = useCallback(async (invoiceId) => {
    const data = await bffMutation(ISSUE_INVOICE, { invoiceId });
    const updated = data.accountingIssueInvoice;
    setInvoices(prev => prev.map(inv => inv.id === invoiceId ? { ...inv, ...updated } : inv));
    return updated;
  }, []);

  const getCreditBalance = useCallback(async (patientId) => {
    const data = await bffQuery(PATIENT_CREDIT_BALANCE, { patientId }, 'PatientCreditBalance');
    return data.patientCreditBalance;
  }, []);

  // Settles part of the invoice out of the patient's on-account balance. Reloads:
  // like a payment, it moves the invoice's status and the KPIs.
  const applyCredit = useCallback(async (invoiceId, amountRial, note) => {
    const data = await bffMutation(APPLY_CREDIT, { invoiceId, amountRial, note: note || null }, 'ApplyCreditToInvoice');
    await loadAll();
    return data.applyCreditToInvoice;
  }, [loadAll]);

  // «پیش‌دریافت». `requestId` is the desk's own idempotency key: a deposit
  // refused for want of an open drawer is retried with the SAME id once one is
  // open, and the service records one credit rather than two (§7.3).
  const recordCredit = useCallback(async ({ patientId, amountRial, paymentMethod, planRef, note, requestId }) => {
    const data = await bffMutation(RECORD_PATIENT_CREDIT, { patientId, amountRial, paymentMethod: paymentMethod || null, planRef: planRef || null, note: note || null, requestId: requestId || null }, 'RecordPatientCredit');
    return data.recordPatientCredit;
  }, []);

  const openShift = useCallback(async () => {
    const data = await bffMutation(OPEN_SHIFT, {});
    return data.accountingOpenShift;
  }, []);

  const closeShift = useCallback(async (shiftId, notes) => {
    const data = await bffMutation(CLOSE_SHIFT, { shiftId, notes });
    return data.accountingCloseShift;
  }, []);

  const getShiftSummary = useCallback(async (shiftId) => {
    const data = await bffQuery(SHIFT_SUMMARY, { shiftId });
    return data.accountingShiftSummary;
  }, []);

  return {
    invoices,          // the active tab's page
    total: meta.total, // size of the active tab (for its pager)
    counts: meta.counts,       // { queue, partial, settled } — the tab badges
    dueQueue: meta.dueQueue,   // «مجموع بدهی» over the range
    collected: meta.collected, // «دریافتی» over the range
    dailySummary,
    loading,
    error,
    reload: loadAll,
    getInvoice,
    getPayments,
    getShiftPayments,
    createInvoice,
    addLineItem,
    updateLineItem,
    overrideLineInsurance,
    removeLineItem,
    setInvoiceDiscount,
    recordPayment,
    voidInvoice,
    refundPayment,
    refundAllAndVoid,
    resolvePayment,
    issueInvoice,
    getCreditBalance,
    applyCredit,
    recordCredit,
    openShift,
    closeShift,
    getShiftSummary,
  };
}

// ── One patient's financial record ────────────────────────────────────────────

const PATIENT_ACCOUNT = `
  query PatientAccount($patientId: ID!) {
    patientAccount(patientId: $patientId) {
      patientId
      balance { currency patientId outstanding totalBilled totalPaid openInvoices }
      invoices { ${INVOICE_FIELDS} }
      payments { ${PAYMENT_FIELDS} }
    }
  }
`;

/**
 * Every invoice raised against a patient and every payment they have made, with the
 * balance the two imply. `patientId` falsy = idle (the drawer is closed).
 *
 * All money in the returned data is RIALS — convert with lib/money at the point of display.
 */
export function usePatientAccount(patientId) {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const load = useCallback(async () => {
    if (!patientId) { setAccount(null); return; }
    setLoading(true);
    setError(null);
    try {
      const data = await bffQuery(PATIENT_ACCOUNT, { patientId }, 'PatientAccount');
      setAccount(data.patientAccount || null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  return { account, loading, error, reload: load };
}

// ── The clinic's payments ledger ──────────────────────────────────────────────

// Deliberately NOT PAYMENT_FIELDS: the joined fields cost the BFF a patient and an
// invoice lookup, and the shift log and the record-payment mutation must not pay for
// columns only the ledger renders.
const PAYMENT_ROW_FIELDS = `
  currency id invoiceId patientId amount paymentMethod referenceNumber createdAt shiftId
  patientName patientPhone doctorId doctorName invoiceStatus
  status voidedAt voidedBy voidReason shiftStatus
  kind refundOf refundedAmount
`;

// includeVoided: this is the ONE view that shows a reversal. Everywhere else a voided
// payment is money that never arrived, and the BFF hides it by default.
//
// Server-PAGED and server-FILTERED: doctor, method and the name search are all applied
// by the service now, so the page, the total and the KPI aggregates all describe the
// same filtered set — and «همه» never pulls a clinic's every receipt into one response
// (the RESOURCE_EXHAUSTED a year of migrated payments triggered). total/collected/… are
// over the WHOLE filtered set; items is just the requested page.
const CLINIC_PAYMENTS_PAGE = `
  query ClinicPaymentsPage($patientId: ID, $from: String, $to: String, $doctorId: String, $method: String, $search: String, $limit: Int, $offset: Int) {
    accountingPaymentsPage(patientId: $patientId, from: $from, to: $to, doctorId: $doctorId, method: $method, search: $search, includeVoided: true, limit: $limit, offset: $offset) {
      currency
      items { ${PAYMENT_ROW_FIELDS} }
      total collectedTotal cashTotal nonCashTotal voidedCount
    }
  }
`;

const EMPTY_PAGE = { items: [], total: 0, collectedTotal: 0, cashTotal: 0, nonCashTotal: 0, voidedCount: 0 };

// `$reason` is String! because the schema's argument is String! — a nullable
// variable in a non-null position is refused by GraphQL's VariablesInAllowedPosition
// rule at VALIDATION, before any resolver runs, so declaring it `String` failed
// every «ابطال» regardless of what the operator typed. Same declaration as the
// refund below, and as voidBillPayment in useBills.js.
const VOID_PAYMENT = `
  mutation AccountingVoidPayment($paymentId: ID!, $reason: String!) {
    accountingVoidPayment(paymentId: $paymentId, reason: $reason) { ${PAYMENT_ROW_FIELDS} }
  }
`;

// Money back: a signed refund row against the payment, into today's drawer.
// `reason` is MANDATORY — money going back out is a judgement, and the ledger
// records it with its justification or not at all. `requestId` is the retry
// guard: a refund the operator could not see the outcome of must not be given
// twice (docs/CLINIC_RECEPTION_FINANCIAL_EVENTS_DESIGN.md §7.3).
const REFUND_PAYMENT = `
  mutation AccountingRefundPayment($paymentId: ID!, $amount: Float!, $paymentMethod: String, $referenceNumber: String, $reason: String!, $requestId: ID) {
    accountingRefundPayment(paymentId: $paymentId, amount: $amount, paymentMethod: $paymentMethod, referenceNumber: $referenceNumber, reason: $reason, requestId: $requestId) { ${PAYMENT_ROW_FIELDS} }
  }
`;

/**
 * Every payment the clinic has taken in a window — the cross-patient counterpart of
 * usePatientAccount, which shows the same receipts for one person.
 *
 * @param {{fromTs: number, toTs: number}|null} range Epoch-ms window; null = no bound
 *   («همه»). CONTROLLED by the caller, and depended on by identity rather than by
 *   reference — the page rebuilds the object every render and depending on the object
 *   would refetch forever (the same trap useCashier documents).
 * @param {string} [patientId] Narrow to one patient, for the drawer's «همه پرداخت‌ها».
 *
 * All money is RIALS. Convert at the point of display with lib/money.
 */
export function useClinicPayments(range = null, patientId = '', opts = {}) {
  const { page = 1, pageSize = 50, doctorId = '', method = '', search = '' } = opts;
  const [data, setData]   = useState(EMPTY_PAGE);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const fromTs = range?.fromTs ?? null;
  const toTs   = range?.toTs ?? null;

  // Widening the window or changing the page fires a query while another is still in
  // flight; the loser must not land on top of the winner.
  const reqId = useRef(0);

  // The window/patient/doctor/method/search vars go to the server; changing any of them
  // is a new filtered set and refetches. The page/pageSize drive the offset.
  const load = useCallback(async () => {
    const req = ++reqId.current;
    setLoading(true);
    setError(null);
    const win = fromTs != null && toTs != null
      ? { from: String(fromTs), to: String(toTs) }
      : {};
    try {
      const d = await bffQuery(
        CLINIC_PAYMENTS_PAGE,
        {
          patientId: patientId || null,
          doctorId: doctorId || null,
          method: method || null,
          search: search || null,
          limit: pageSize,
          offset: (Math.max(1, page) - 1) * pageSize,
          ...win,
        },
        'ClinicPaymentsPage',
      );
      if (req !== reqId.current) return;   // superseded — drop it
      setData(d.accountingPaymentsPage || EMPTY_PAGE);
    } catch (e) {
      if (req !== reqId.current) return;
      setError(e);
    } finally {
      if (req === reqId.current) setLoading(false);
    }
  }, [fromTs, toTs, patientId, page, pageSize, doctorId, method, search]);

  useEffect(() => { load(); }, [load]);

  /**
   * Reverse a payment taken in error. There is no delete: the row survives, marked,
   * and every total stops counting it — so the invoice it settled reopens and the cash
   * drawer un-counts it, server-side. Re-fetch the current page: the void changes the
   * KPI aggregates (which are now the server's, not summed from the rows on screen), and
   * refetching with the same page param keeps the user exactly where they were.
   */
  const voidPayment = useCallback(async (paymentId, reason) => {
    // Never null: the argument is non-null, and an unexplained void is an empty
    // reason, not an absent one.
    const d = await bffMutation(VOID_PAYMENT, { paymentId, reason: reason || '' }, 'AccountingVoidPayment');
    await load();
    return d.accountingVoidPayment;
  }, [load]);

  /**
   * Hand money back. Unlike a void — which says the payment should never have been
   * taken — a refund is a NEW signed row against a real payment, banked into today's
   * drawer, so a closed shift is never rewritten and both facts stay in the ledger.
   */
  const refundPayment = useCallback(async (paymentId, { amount, paymentMethod, referenceNumber, reason, requestId } = {}) => {
    const d = await bffMutation(
      REFUND_PAYMENT,
      { paymentId, amount, paymentMethod: paymentMethod || null, referenceNumber: referenceNumber || null, reason: reason || '', requestId: requestId || null },
      'AccountingRefundPayment',
    );
    await load();
    return d.accountingRefundPayment;
  }, [load]);

  // CSV export of the filtered set — one bounded fetch (a generous cap, NOT the whole
  // table), so a huge ledger degrades to "export is capped" rather than exhausting.
  const fetchForExport = useCallback(async () => {
    const win = fromTs != null && toTs != null
      ? { from: String(fromTs), to: String(toTs) }
      : {};
    const d = await bffQuery(
      CLINIC_PAYMENTS_PAGE,
      {
        patientId: patientId || null,
        doctorId: doctorId || null,
        method: method || null,
        search: search || null,
        limit: 5000,
        offset: 0,
        ...win,
      },
      'ClinicPaymentsPage',
    );
    return d.accountingPaymentsPage?.items || [];
  }, [fromTs, toTs, patientId, doctorId, method, search]);

  // KPI figures over the WHOLE filtered set, from the server. count is the LIVE payment
  // count: total_count includes the voided rows the ledger shows, so subtract them.
  const totals = {
    collected: data.collectedTotal,
    cash:      data.cashTotal,
    nonCash:   data.nonCashTotal,
    count:     Math.max(0, (data.total || 0) - (data.voidedCount || 0)),
    voided:    data.voidedCount || 0,
  };

  return { payments: data.items, total: data.total || 0, totals, loading, error, reload: load, voidPayment, refundPayment, fetchForExport };
}

// The clinic's declared services, for pricing a medical service line at the cash desk.
// Hook name kept for its importers; the underlying query is now clinicServiceDeclarations.
export function useMedicalServiceTariffs() {
  const [tariffs, setTariffs] = useState([]);
  useEffect(() => {
    bffQuery(CLINIC_SERVICE_DECLARATIONS)
      .then(d => setTariffs(d.clinicServiceDeclarations || []))
      .catch(() => {});
  }, []);
  return tariffs;
}

/** Doctors of the caller's clinic, for the cash desk's doctor filter. */
export function useClinicDoctorOptions() {
  const [doctors, setDoctors] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const clinic = await bffQuery(MY_CLINIC, {}, 'MyClinic');
        const clinicId = clinic?.myClinic?.id;
        if (!clinicId) return;
        const data = await bffQuery(CLINIC_DOCTOR_OPTIONS, { clinicId }, 'MyClinicDoctors');
        if (cancelled) return;
        setDoctors((data?.listClinicDoctors || []).filter(d => d.active && d.doctorId));
      } catch {
        // The filter is a refinement, not a gate — an empty list just means no dropdown.
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return doctors;
}
